import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSites, useSystem } from "@/lib/hooks";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { StackBadge } from "@/components/StackBadge";
import { UploadCloud, Search, Rocket } from "lucide-react";

export default function SitesPage() {
  const { data: sites, isLoading } = useSites();
  const { data: sys } = useSystem();
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    if (!sites) return [];
    const t = q.trim().toLowerCase();
    if (!t) return sites;
    return sites.filter(
      (s) =>
        s.name.toLowerCase().includes(t) ||
        s.subdomain.toLowerCase().includes(t) ||
        s.stack_type.toLowerCase().includes(t)
    );
  }, [sites, q]);

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Sites</h1>
          <p className="text-muted-foreground mt-1">All uploaded projects and their runtime state.</p>
        </div>
        <Link to="/upload">
          <Button data-testid="sites-upload-button" className="gap-2">
            <UploadCloud className="h-4 w-4" /> Upload ZIP
          </Button>
        </Link>
      </header>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="sites-search-input"
            placeholder="Search sites by name, subdomain, stack…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="text-xs text-muted-foreground">
          {filtered.length} of {sites?.length || 0}
        </div>
      </div>

      <Card data-testid="sites-table" className="border-border overflow-hidden p-0">
        <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground border-b border-border surface-1">
          <div className="col-span-5">Site</div>
          <div className="col-span-3">Stack</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Updated</div>
        </div>

        {isLoading && (
          <div className="p-6 text-sm text-muted-foreground">Loading sites…</div>
        )}

        {!isLoading && filtered.length === 0 && (
          <div className="p-10 text-center">
            <div className="mx-auto h-10 w-10 rounded-lg surface-2 grid place-items-center mb-3">
              <Rocket className="h-5 w-5" />
            </div>
            <div className="font-medium">No sites match</div>
            <div className="text-sm text-muted-foreground mt-1">
              Upload a ZIP to get started.
            </div>
            <div className="mt-4">
              <Link to="/upload">
                <Button className="gap-2">
                  <UploadCloud className="h-4 w-4" /> Upload ZIP
                </Button>
              </Link>
            </div>
          </div>
        )}

        {filtered.map((s) => (
          <Link
            key={s.id}
            to={`/sites/${s.id}`}
            data-testid={`site-row-${s.slug}`}
            className="grid grid-cols-12 px-4 py-4 items-center border-b border-border last:border-b-0 hover:bg-[hsl(var(--surface-2))]/40 transition-colors"
          >
            <div className="col-span-5 min-w-0">
              <div className="font-medium truncate">{s.name}</div>
              <div className="text-xs text-muted-foreground font-mono truncate">
                {s.subdomain}.{sys?.base_domain || "emhost.local"}
              </div>
            </div>
            <div className="col-span-3">
              <StackBadge type={s.stack_type} />
            </div>
            <div className="col-span-2">
              <StatusBadge status={s.status} />
            </div>
            <div className="col-span-2 text-right text-xs text-muted-foreground">
              {s.updated_at ? new Date(s.updated_at).toLocaleString() : "—"}
            </div>
          </Link>
        ))}
      </Card>
    </div>
  );
}
